import React, { useState } from 'react';

import { classNames } from '@/helpers/classNames';

import styles from './Tabs.module.scss';

type TabItem = {
	title: string;
	tabComponent?: React.ReactNode;
	trailingComponent?: React.ReactNode;
	hidden?: boolean;
};

type TabsProps = {
	items: TabItem[];
	className?: string;
	maskClassName?: string;
	initialSelectedIndex?: number;
	selectedIndex?: number;
	onChange?: (selectedIndex: number) => void;
	contentClassName?: string;
	stickyBar?: boolean;
	stickyBarClassName?: string;
	renderContent?: (args: {
		selectedIndex: number;
		selectedItem?: TabItem;
		items: TabItem[];
		setSelectedIndex: (index: number) => void;
	}) => React.ReactNode;
};

export const Tabs: React.FC<TabsProps> = ({
	className,
	maskClassName,
	initialSelectedIndex,
	selectedIndex: selectedIndexProp,
	onChange,
	items,
	contentClassName,
	stickyBar,
	stickyBarClassName,
	renderContent,
}) => {
	const [internalSelectedIndex, setInternalSelectedIndex] = useState(
		initialSelectedIndex || 0,
	);
	const selectedIndex = selectedIndexProp ?? internalSelectedIndex;
	const handleSelect = (index: number) => {
		if (selectedIndexProp === undefined) {
			setInternalSelectedIndex(index);
		}
		if (onChange) {
			onChange(index);
		}
	};
	const content = renderContent
		? renderContent({
				selectedIndex,
				selectedItem: items[selectedIndex],
				items,
				setSelectedIndex: handleSelect,
			})
		: items[selectedIndex]?.tabComponent;

	const bar = (
		<>
			<div className={classNames([styles['mask'], maskClassName])} />
			<div className={styles['row']}>
				{items.map((item, index) => {
					return !item.hidden ? (
						<div
							key={`tab-button-${index}`}
							className={classNames([
								styles['tab'],
								selectedIndex === index && styles['active'],
							])}
							onClick={() => {
								handleSelect(index);
							}}
						>
							<div>{item.title}</div>
							{!!item.trailingComponent && item.trailingComponent}
						</div>
					) : null;
				})}
			</div>
		</>
	);

	return (
		<div className={classNames([styles['tabs'], className])}>
			{stickyBar ? (
				<div className={classNames([styles['sticky-bar'], stickyBarClassName])}>{bar}</div>
			) : bar}
			{!!content ? (
				<div
					className={classNames([
						styles['content'],
						contentClassName,
					])}
				>
					{content}
				</div>
			) : null}
		</div>
	);
};

export const TabsCount: React.FC<{ count: number }> = ({ count }) => (
	<div className={styles['count']}>
		<div className={styles['number']}>{count}</div>
	</div>
);
